import {
  TranslationData,
  BadgeId,
  Age,
  Profile,
  isTranslationData,
  isAges,
  isProfiles,
  Question,
  QuestionKey,
  QuestionId,
  QuizModuleId,
  QuizModule,
} from "@common";
import getQuizModuleSlides from "@natklok/util/getQuizModuleSlides";
import { getStoredQuizModuleData } from "@natklok/util/quizModuleUtils";
import { badgeIds } from "../config";
import {
  SlideData,
  QuestionState,
  // isQuestionState,
  QuestionVariant,
  QuizSlideDataSequence,
} from "../interfaces/question.interface";

import { getStoredQuestions } from "../util/questionUtils";

/**
 * // TODO: The reducer can be made more efficient if the state's
 * default value is an empty list. When profiles and age are chosen,
 * it should filter questions, and THEN wrap them with SlideData.
 *
 * Current order:
 * - state is set to all possible questions
 * - filterByKeys is called, state is filtered by age and profiles
 *
 * Proposed order:
 * - state is set to empty list
 * - a new Action is called, which filters questions and wraps the
 *   filtered questions with SlideData
 *
 */

enum ActionType {
  filterByKeys = "filterByKeys",
  getQuestion = "getQuestion",
  syncWithStorage = "syncWithStorage",
  reset = "reset",
  changeQuestionState = "changeQuestionState",
  completeCurrentQuestion = "completeCurrentQuestion",
  setQuestionsSavedInStorage = "setQuestionsSavedInStorage",
  removeQuestionsWithSameVariant = "removeQuestionsWithSameVariant",
  shuffle = "shuffle",
  slice = "slice",
  generateQuiz = "generateQuiz",
}

interface QuestionFilter {
  variant?: TranslationData;
  id?: TranslationData;
  badge?: Array<BadgeId>;
  fitsAges?: Array<Age>;
  fitsProfiles?: Array<Profile>;
}

export type Action =
  | {
      type: "filterByKeys";
      filter: QuestionFilter;
    }
  | {
      type: "getQuestion";
      id: string;
    }
  | { type: "reset"; defaultSlideDatas: QuizSlideDataSequence }
  | { type: "shuffle" }
  | { type: "syncWithStorage"; profiles: Array<Profile>; age: Age; questions: Question[] }
  | { type: "removeQuestionsWithSameVariant" }
  | { type: "setQuestionsSavedInStorage"; ids: Array<string> }
  | { type: "slice"; nOfQuestions: number }
  | {
      type: "changeQuestionState";
      newState: QuestionState;
      id?: string;
      index?: number;
    }
  | { type: "completeCurrentQuestion" }
  | { type: "setQuizSlideDataSequence"; quizSlideDataSequence: QuizSlideDataSequence }
  | {
      type: "updateAnswerStatsFromServerQuizSlideDataSequence";
      quizSlideDataSequence: QuizSlideDataSequence;
    }
  | { type: "completeQuestionsToIndex"; index: number }
  | {
      type: "generateQuiz";
      age: Age;
      profiles: Array<Profile>;
      quizModule: QuizModuleId;
      questions: Question[];
      quizModules: Record<string, QuizModule>;
    }
  | {
      type: "generateOneQuestionQuiz";
      age: Age;
      profiles: Array<Profile>;
      questionId: QuestionId;
      defaultSlideDatas: QuizSlideDataSequence;
    }
  | {
      type: "generateFewIdQuiz";
      age: Age;
      profiles: Array<Profile>;
      questionIds: QuestionId[];
      defaultSlideDatas: QuizSlideDataSequence;
    };

const isBadgeIds = (a: any): a is Array<BadgeId> => {
  if (!Array.isArray(a)) return false;
  return a.every((elem: string) => badgeIds.some((id) => id === elem));
};

const isQuestionFilter = (obj: object): obj is QuestionFilter => {
  if (!obj) return false;
  for (const [key, value] of Object.entries(obj)) {
    if (!value) return false;
    switch (key) {
      case "variant":
        if (!isTranslationData(value)) {
          return false;
        }
        continue;
      case "id":
        if (!isTranslationData(value)) {
          return false;
        }
        continue;
      case "fitsAges":
        if (!isAges(value)) {
          return false;
        }
        continue;
      case "fitsProfiles":
        if (!isProfiles(value)) {
          return false;
        }
        continue;
      case "badge":
        if (!isBadgeIds(value)) {
          return false;
        }
        continue;
      default:
        return false;
    }
  }
  return true;
};

const shuffleArray = (array: Array<any>) => {
  for (let i = array.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
};

export const getDefaultSlideDatas = (questions: Question[]): QuizSlideDataSequence => {
  return questions.map((q: Question) => ({
    question: { ...q },
    state: "unanswered",
    completed: false,
    savedInStorage: false,
  }));
};

export const questionsReducer = (
  _state: QuizSlideDataSequence,
  action: Action
): QuizSlideDataSequence => {
  switch (action.type) {
    case "generateQuiz": {
      console.log(action);
      const storedQuizModuleData = getStoredQuizModuleData({
        quizModule: action.quizModule,
        quizModules: action.quizModules,
      });
      console.log("storedQuizModuleData: ", storedQuizModuleData);
      let incorrectlyAnsweredSlides = storedQuizModuleData?.getIncorrectlyAnsweredSlides({
        age: action.age,
        questions: action.questions,
        quizModules: action.quizModules,
      });

      // If no question where generated just show the old entire quiz, even if they are answered
      if (incorrectlyAnsweredSlides.length === 0)
        incorrectlyAnsweredSlides = getQuizModuleSlides({
          quizModuleId: action.quizModule,
          age: action.age,
          questions: action.questions,
          quizModules: action.quizModules,
        });

      console.log("incorrectlyAnsweredSlides: ", incorrectlyAnsweredSlides);

      return incorrectlyAnsweredSlides;
    }
    case "generateOneQuestionQuiz": {
      const slides = action.defaultSlideDatas;
      for (let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        if (
          slide.question?.id.sv === action.questionId ||
          slide.question?.id.en === action.questionId
        ) {
          return [slide];
        }
      }

      return [];
    }
    case "generateFewIdQuiz": {
      const slides = action.defaultSlideDatas;
      const quizSlides = [];
      for (let j = 0; j < action.questionIds.length; j++) {
        const idOrVariant = action.questionIds[j];
        for (let i = 0; i < slides.length; i++) {
          const slide = slides[i];
          if (slide.question === undefined) continue;
          if (
            slide.question.id.sv === idOrVariant ||
            slide.question.id.en === idOrVariant ||
            slide.question.variant.sv === idOrVariant ||
            slide.question.variant.en === idOrVariant
          ) {
            quizSlides.push(slide);
            continue;
          }
        }
      }

      console.log("quizSlides: ", quizSlides);

      return quizSlides;
    }
    case "completeQuestionsToIndex": {
      let i = -1;
      return _state.map((qw: SlideData) => {
        i += 1;
        if (i < action.index) {
          return { ...qw, completed: true };
        } else {
          return qw;
        }
      });
    }
    case "setQuizSlideDataSequence": {
      const newState: QuizSlideDataSequence = [...action.quizSlideDataSequence];
      return newState;
    }
    case "updateAnswerStatsFromServerQuizSlideDataSequence": {
      const newSlideData: QuizSlideDataSequence = [..._state];
      newSlideData.forEach((slideData: SlideData, index: number) => {
        const serverSlideData: SlideData | undefined = action.quizSlideDataSequence[index];
        if (serverSlideData !== undefined) {
          slideData.answerStat = serverSlideData.answerStat;
        }
      });
      return newSlideData;
    }
    case "filterByKeys": {
      const filter = action.filter;
      if (!isQuestionFilter(filter)) return _state;
      const senap = _state.filter((qw: SlideData) => {
        let questionIsValid = true;
        for (const [filterKey, filterValue] of Object.entries(filter)) {
          let valueToFilter: any;
          if (qw.question) valueToFilter = qw.question[filterKey as QuestionKey];

          if (isTranslationData(valueToFilter)) {
            if (!Object.values(valueToFilter).includes(filterValue)) {
              questionIsValid = false;
            }
          } else if (isAges(filterValue)) {
            if (!filterValue.every((f) => isAges(valueToFilter) && valueToFilter.includes(f))) {
              questionIsValid = false;
            }
          } else if (isProfiles(filterValue)) {
            if (!filterValue.some((f) => isProfiles(valueToFilter) && valueToFilter.includes(f))) {
              questionIsValid = false;
            }
          } else if (isBadgeIds(filterValue)) {
            if (Array.isArray(valueToFilter)) {
              if (
                !filterValue.some((badgeIdForQuestion: BadgeId) => {
                  return valueToFilter.some(
                    (badgeIdInFilter: BadgeId) => badgeIdForQuestion === badgeIdInFilter
                  );
                })
              )
                questionIsValid = false;
            } else if (typeof valueToFilter === "string") {
              if (
                !filterValue.some((badgeIdInFilter: BadgeId) => badgeIdInFilter === valueToFilter)
              )
                questionIsValid = false;
            } else {
              questionIsValid = false;
            }
          }
        }
        return questionIsValid;
      });
      return senap;
    }
    case "getQuestion": {
      if (typeof action.id !== "string") {
        console.error('questionsReducer("getQuestion") error : id is not string');
        return _state;
      }
      const res = _state.find((qw: SlideData) => {
        if (qw.question) {
          return Object.values(qw.question.id).includes(action.id);
        }
      });
      if (res) {
        return [res];
      } else {
        console.error('questionsReducer("getQuestion") error : couldn\'t find question');
        return _state;
      }
    }
    case "reset":
      return action.defaultSlideDatas;
    case "changeQuestionState":
      if (action.id === undefined && action.index === undefined) {
        console.error(
          'questionsReducer("changeQuestionState") error : a id or index must be provided'
        );
        return _state;
      }

      // if (!isQuestionState(action.newState)) {
      //   console.error('questionsReducer("changeQuestionState") error : newState is not valid');
      //   return _state;
      // }

      if (action.id !== undefined) {
        const id = action.id;
        return _state.map((qw: SlideData) => {
          if (qw.question && Object.values(qw.question.id).includes(id)) {
            return { ...qw, state: action.newState };
          } else {
            return qw;
          }
        });
      } else if (action.index !== undefined) {
        const newState = [..._state];
        newState[action.index].state = action.newState;

        return newState;
      } else {
        return _state;
      }

    case "completeCurrentQuestion": {
      let setOnce = false;
      // Set the question current question (the first one that isn't completed)
      return _state.map((qw: SlideData) => {
        if (setOnce === false && qw.completed === false) {
          setOnce = true;
          const newState = { ...qw, completed: true };

          return newState;
        } else {
          return qw;
        }
      });
    }
    case "setQuestionsSavedInStorage":
      if (action.ids.every((i) => typeof i === "string")) {
        console.error(
          'questionsReducer("setQuestionsSavedInStorage") error : ids is not a list of strings'
        );
        return _state;
      }
      return _state.map((qw: SlideData) => {
        if (action.ids.some((id) => qw.question && Object.values(qw.question.id).includes(id))) {
          return { ...qw, savedInStorage: true };
        } else {
          return qw;
        }
      });
    case "removeQuestionsWithSameVariant": {
      const quesionVariantsAndIds: Record<QuestionVariant, Set<QuestionId>> = {};
      for (const qw of _state) {
        if (qw.question) {
          const val = quesionVariantsAndIds[qw.question.variant.sv];
          if (val) val.add(qw.question.id.sv);
          else quesionVariantsAndIds[qw.question.variant.sv] = new Set([qw.question.id.sv]);
        }
      }
      let svIdsToRemove: Array<QuestionId> = [];
      for (const ids of Object.values(quesionVariantsAndIds)) {
        const idsAsArr = [...ids];
        const keepQuestionWithIndex = Math.floor(Math.random() * idsAsArr.length);
        idsAsArr.splice(keepQuestionWithIndex, 1);
        svIdsToRemove = [...svIdsToRemove, ...idsAsArr];
      }
      return _state.filter((qw) => qw.question && !svIdsToRemove.includes(qw.question.id.sv));
    }
    case ActionType.syncWithStorage: {
      const correctSlideDatas = getStoredQuestions({
        correctOrIncorrect: "correct",
        profiles: action.profiles,
        age: action.age,
        questions: action.questions,
      });
      if (correctSlideDatas) {
        const variants: Array<QuestionVariant> = [];
        for (const storedCorrectQuestion of correctSlideDatas) {
          if (storedCorrectQuestion.question)
            variants.push(storedCorrectQuestion.question.variant.sv);
        }
        return _state.filter((qw) => qw.question && !variants.includes(qw.question.variant.sv));
      } else {
        return _state;
      }
    }
    case ActionType.shuffle:
      shuffleArray(_state);
      return _state.sort((a, b) => (b.completed === true ? -1 : 1));
    case ActionType.slice:
      return _state.slice(0, action.nOfQuestions);
    default:
      return _state;
  }
};
